import { SeasonService } from '@/services/season-service';
import { TaskService } from '@/services/task-service';
import { LogEntryService } from '@/services/log-entry-service';
import type { Season } from '@/types/season';
import type { Task } from '@/types/task';
import type { LogEntry } from '@/types/log-entry';

// ── Countdown season selection (J-07) ─────────────────────────────────────────
// Rule: primary flag wins; fallback to earliest opener among active/upcoming seasons.

/** Returns true when a season is active (opener passed, not yet closed) or upcoming. */
function isActiveOrUpcoming(season: Season): boolean {
  const today = new Date().toISOString().slice(0, 10);
  // A season is eligible when it has not ended: no closer date, or closer date is today or later.
  // Upcoming seasons (openerDate > today) also satisfy this because notClosed covers both cases.
  return !season.closerDate || season.closerDate >= today;
}

/** Sort comparator: earliest opener first. */
function byEarliestOpener(a: Season, b: Season): number {
  return a.openerDate.localeCompare(b.openerDate);
}

/**
 * Returns the season that should drive the Dashboard CountdownWidget.
 *
 * Selection rule (J-07):
 *   1. Among active-or-upcoming seasons, return the one flagged isPrimary.
 *   2. If none is flagged, return the earliest-opener active-or-upcoming season.
 *   3. If no active-or-upcoming seasons exist, return null.
 *
 * Single-season users: the sole season is returned (primary flag is implicit).
 */
export function getCountdownSeason(seasons: Season[]): Season | null {
  const eligible = seasons.filter(isActiveOrUpcoming);
  if (eligible.length === 0) return null;
  const primary = eligible.find((s) => s.isPrimary === true);
  if (primary) return primary;
  return eligible.sort(byEarliestOpener)[0] ?? null;
}

/**
 * Returns all tasks for a given season — full set including completed.
 * Used by CountdownWidget (total denominator) and PrepCenter (visible slice).
 * Both widgets must read from this function so the denominator is always consistent.
 */
export function selectSeasonTasksForDashboard(seasonId: string, tasks: Task[]): Task[] {
  return tasks.filter((t) => t.seasonId === seasonId);
}

export interface SeasonTaskStats {
  seasonId: string;
  seasonName: string;
  total: number;
  completed: number;
  overdue: number;
  blocked: number;
}

export interface DashboardData {
  seasons: Season[];
  upcomingTasks: Task[];
  taskStats: SeasonTaskStats[];
  recentLogEntries: LogEntry[];
}

export class DashboardService {
  constructor(
    private seasonService: SeasonService,
    private taskService: TaskService,
    private logEntryService: LogEntryService,
  ) {}

  async getDashboardData(): Promise<DashboardData> {
    const today = new Date().toISOString().slice(0, 10);

    const [seasons, rawUpcomingTasks, recentLogEntries] = await Promise.all([
      this.seasonService.getSeasons(),
      this.taskService.getUpcomingTasks(20),
      this.logEntryService.getRecentEntries(10),
    ]);

    // getUpcomingTasks excludes completed but not snoozed — filter snoozed here
    const upcomingTasks = rawUpcomingTasks.filter(
      (t) => !t.snoozedUntil || t.snoozedUntil < today,
    );

    // Sort seasons nearest opener first
    const sortedSeasons = [...seasons].sort((a, b) => a.openerDate.localeCompare(b.openerDate));

    // Compute task stats per season (requires full task set, including completed)
    const taskStats = await Promise.all(
      sortedSeasons.map(async (season): Promise<SeasonTaskStats> => {
        const tasks = await this.taskService.getTasks(season.id);
        const total = tasks.length;
        const completed = tasks.filter((t) => t.completed).length;
        const overdue = tasks.filter((t) => !t.completed && t.dueDate < today).length;
        const blocked = tasks.filter((t) => !!t.blocked && !t.completed).length;
        return { seasonId: season.id, seasonName: season.name, total, completed, overdue, blocked };
      }),
    );

    return { seasons: sortedSeasons, upcomingTasks, taskStats, recentLogEntries };
  }
}
